import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Box,
} from "@mui/material";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { SoftButton, useDialogPaperSx } from "../../hatim/soft/SoftKit";
import { isRangeType } from "../../../lib/hatimType";
import {
  useAddHatimUserMutation,
  useUpdateHatimUserAssignmentMutation,
} from "../../../api/hatimManagementApi";
import PageRangeEditor from "../../hatim/components/PageRangeEditor";
import { hasRangeConflict, maxAssignable, takenRanges } from "../../../lib/hatimAssignment";
import { format } from "../../../translations";

function isRowValid(row, maxPage) {
  const s = Number(row.startPage);
  const e = Number(row.endPage);
  if (!Number.isFinite(s) || !Number.isFinite(e)) return false;
  if (s < 1 || e < 1 || s > e) return false;
  if (maxPage > 0 && (s > maxPage || e > maxPage)) return false;
  return true;
}

function initialRows(member) {
  const a = member?.assignedPageRanges;
  if (!Array.isArray(a)) return [];
  return a.map((r) => ({ startPage: String(r.startPage), endPage: String(r.endPage) }));
}

export default function HatimUserAssignmentModal({
  open,
  mode,
  hatimTemplateId,
  type,
  totalAmount,
  member,
  users,
  onClose,
  onSaved,
}) {
  const { t } = useScriptMode();
  const paperSx = useDialogPaperSx();
  const [addUser, { isLoading: adding }] = useAddHatimUserMutation();
  const [updateAssignment, { isLoading: updating }] = useUpdateHatimUserAssignmentMutation();

  const isRange = isRangeType(type);
  const isEdit = mode === "edit";
  const isLoading = adding || updating;
  const maxPage = Number(totalAmount || 0);

  const [email, setEmail] = useState(member?.email || "");
  const [amount, setAmount] = useState(String(member?.assignedAmount ?? ""));
  const [rows, setRows] = useState(initialRows(member));
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setEmail(member?.email || "");
      setAmount(String(member?.assignedAmount ?? ""));
      setRows(initialRows(member));
      setError("");
    }
  }, [open, member]);

  const trimmedEmail = email.trim();
  const amountNum = Number(amount);
  const amountValid =
    String(amount).trim() !== "" && Number.isFinite(amountNum) && amountNum >= 1;
  const rangesValid = rows.length > 0 && rows.every((r) => isRowValid(r, maxPage));
  const emailValid = isEdit ? true : !!trimmedEmail && /@/.test(trimmedEmail);

  // Kalan miktar sınırı: adet tipinde üst limit, aralık tipinde çakışma kontrolü.
  const editingEmail = isEdit ? member?.email : null;
  const maxAmount = maxAssignable(users, type, totalAmount, editingEmail);
  const amountExceeds = amountValid && Number.isFinite(maxAmount) && amountNum > maxAmount;
  const taken = isRange ? takenRanges(users, editingEmail) : [];
  const rangeConflict = isRange && hasRangeConflict(rows, taken);

  const canSubmit =
    emailValid &&
    (isRange ? rangesValid && !rangeConflict : amountValid && !amountExceeds) &&
    !isLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const targetEmail = isEdit ? member.email : trimmedEmail;
    const cleanRanges = isRange
      ? rows
          .filter((r) => isRowValid(r, maxPage))
          .map((r) => ({ startPage: Number(r.startPage), endPage: Number(r.endPage) }))
      : undefined;
    const body = {
      hatimTemplateId,
      email: targetEmail,
      assignedAmount: isRange ? undefined : amountNum,
      assignedPageRanges: cleanRanges,
    };
    try {
      const fn = isEdit ? updateAssignment : addUser;
      await fn(body).unwrap();
      onSaved && onSaved();
      onClose && onClose();
    } catch (err) {
      const msg = err?.message || err?.data?.message || t("hatim_mgmt_action_error");
      setError(msg);
    }
  };

  const title = isEdit
    ? t("hatim_mgmt_edit_user_modal_title")
    : t("hatim_mgmt_add_user_modal_title");

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: paperSx }}>
      <DialogTitle sx={{ fontWeight: 500 }}>{title}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {!isEdit ? (
            <TextField
              autoFocus
              type="email"
              label={t("hatim_mgmt_add_user_email_label")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          ) : (
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {member?.email}
            </Typography>
          )}
          {isRange ? (
            <>
              {taken.length > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {t("hatim_mgmt_ranges_taken_label")}
                </Typography>
              ) : null}
              <PageRangeEditor
                assignedRanges={taken.length > 0 ? taken : null}
                rows={rows}
                onChange={setRows}
                maxPage={maxPage}
              />
              {rangeConflict ? (
                <Typography color="error" variant="body2">
                  {t("hatim_mgmt_ranges_conflict")}
                </Typography>
              ) : null}
            </>
          ) : (
            <TextField
              type="number"
              label={t("hatim_mgmt_add_user_amount_label")}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 1, step: 1, max: Number.isFinite(maxAmount) ? maxAmount : undefined }}
              error={amountExceeds}
              helperText={
                amountExceeds
                  ? format(t("hatim_mgmt_assign_exceeds"), { n: maxAmount })
                  : Number.isFinite(maxAmount)
                  ? format(t("hatim_mgmt_assign_max_hint"), { n: maxAmount })
                  : undefined
              }
            />
          )}
          {error ? (
            <Typography color="error" variant="body2">{error}</Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <SoftButton tone="cancel" onClick={onClose} disabled={isLoading}>
          {t("hatim_mgmt_repeat_cancel")}
        </SoftButton>
        <SoftButton tone="success" onClick={handleSubmit} disabled={!canSubmit}>
          {t("hatim_mgmt_repeat_save")}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}
